import React, { useEffect } from 'react'
import { ThemeProvider } from '@material-ui/core/styles'
import theme from './theme'
import { Route, Switch, useLocation } from 'react-router-dom'
import { setAuthToken } from './app/helpers/AuthTokenHelper'
import CssBaseline from '@material-ui/core/CssBaseline'

// import SignUp from './screens/SignUp'
import WelcomeStepOne from './screens/WelcomeStepOne'
import WelcomeStepTwo from './screens/WelcomeStepTwo'
import WelcomeComplete from './screens/WelcomeComplete'
import Welcome from './screens/Welcome'
import PreSeason from './screens/PreSeason'
import PreSeasonTask from './screens/PreSeasonTask'
import PreSeasonActivity from './screens/PreSeasonActivity'
import Login from './screens/Login'

import { loadMember } from './redux/actions/Member.actions'
import { useDispatch, useSelector } from 'react-redux'
import PrivateRoute from './app/components/PrivateRoute'
import { GTMProvider } from '@elgorditosalsero/react-gtm-hook'
import PreSeasonGuide from './screens/PreSeasonGuide'
import LogoutRoute from './app/components/LogoutRoute'
import SignUp from './screens/SignUp'
import Alert from './app/components/Alert'
import Loader from './app/components/Loader'
import navigateToGoSite from './app/helpers/NavigateToGoSite'

if (localStorage.token) {
  setAuthToken(localStorage.token)
}

const App = () => {
  const gtmParams = {
    id: process.env.REACT_APP_GTM_ID
  }

  const dispatch = useDispatch()
  const { loading, isAuthenticated } = useSelector((state) => state.member)
  const location = useLocation()

  // Whether this browser is holding a session at all. Anything below that would
  // cost an anonymous visitor an auth call is gated on this.
  const hasStoredSession = Boolean(localStorage.getItem('token'))

  // The pages that exist for someone who is not signed in.
  const isGuestRoute =
      location.pathname === '/' || location.pathname === '/login'

  // Try load member on app load:
  //
  // The old guard ended in `!location.pathname === '/'`, which JavaScript reads
  // as `(!location.pathname) === '/'` — always false, so the whole condition was
  // always false and this never ran. That is why a member with a live session
  // could open the site and be shown the signup page as though they were a
  // stranger.
  //
  // The intent behind that clause, not spending an auth call on visitors who
  // came to sign up, is kept by asking only when a session is actually stored.
  useEffect(() => {
    if (
      !loading &&
      !isAuthenticated &&
      hasStoredSession &&
      !location.pathname.includes('logout')
    ) {
      dispatch(loadMember())
    }
  }, [])

  // Someone already signed in who types the bare address wants their dashboard,
  // not the signup page. The dashboard lives on the `go` site, reached with the
  // Rails token in the query string — the same handoff every other link to it
  // uses.
  //
  // Without that token there is nothing to hand over: `go` would turn them
  // straight back, so leave them on the login page, which can get them a new
  // one.
  useEffect(() => {
    if (
        isAuthenticated &&
        !loading &&
        isGuestRoute &&
        localStorage.getItem('auth_token')
    ) {
      window.location.href = navigateToGoSite('dashboard')
    }
  }, [isAuthenticated, loading, isGuestRoute])

  // Hold the guest pages back for the moment the session check takes, or a
  // returning member sees the signup page flash before being sent on. Only for
  // browsers holding a session: a first-time visitor waits for nothing.
  if (hasStoredSession && loading && isGuestRoute) {
    return (
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Loader />
        </ThemeProvider>
    )
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GTMProvider state={gtmParams}>
        <Alert />
        <Switch>
          <Route exact path='/' component={() => <SignUp />} />
          <Route exact path='/signup' component={() => <SignUp />} />
          <Route
            exact
            path='/signup/lifestyle'
            component={() => <SignUp lifestyle={true} />}
          />
          <Route exact path='/login' component={Login} />
          <Route exact path='/logout' component={LogoutRoute} />
          <PrivateRoute exact path='/welcome' component={Welcome} />
          <PrivateRoute
            exact
            path='/welcome/step-1'
            component={WelcomeStepOne}
          />
          <PrivateRoute
            exact
            path='/welcome/step-2'
            component={WelcomeStepTwo}
          />
          <PrivateRoute
            exact
            path='/welcome/complete'
            component={WelcomeComplete}
          />
          <PrivateRoute exact path='/pre-season' component={PreSeason} />
          <PrivateRoute path='/pre-season/tasks' component={PreSeasonTask} />
          <PrivateRoute path='/pre-season/guides' component={PreSeasonGuide} />
          <PrivateRoute
            path='/pre-season/activities'
            component={PreSeasonActivity}
          />
          <Route path='*' component={Login} />
        </Switch>
      </GTMProvider>
    </ThemeProvider>
  )
}

export default App
